import { debounce } from "lodash";
import { Dispatch, useMemo } from "react";
import { warningMessage } from "../../../../helpers/ErrorHandler";
import { downloadFileFromUrl } from "../../../../utils/utils";
import {
  buildCalculatePayload,
  buildCombinedIngredients,
  buildDownloadPayload
} from "../recipeVariant.utils";
import { ManageRecipeVariantAction } from "./manageRecipeVariantReducer";

type UseManageRecipeVariantHandlersParams = {
  recipeId: any;
  dispatch: Dispatch<ManageRecipeVariantAction>;
  recipeData: any;
  recipeDataRefetch: () => void;
  refetchRecipeResults: () => Promise<unknown>;
  pendingDeleteVariantIds: string[];
  deleteIngredient: any | null;
  selectedVariants: any[];
  setSelectedVariants: (value: any[] | ((prev: any[]) => any[])) => void;
  productSize: number;
  recipeResults: any;
  impactDetails: any;
  variants: any[];
  staticRecipeIngredientList: any[];
  deleteRecipeVariant: (params: any, options: any) => void;
  getPdfUrl: (payload: any, options: any) => void;
  calculateRecipe: (params: any, options: any) => void;
  deleteRecipeIngredient: (params: any, options: any) => void;
  updateIngredientPercentage: (data: any, options: any) => void;
};

export const useManageRecipeVariantHandlers = ({
  recipeId,
  dispatch,
  recipeData,
  recipeDataRefetch,
  refetchRecipeResults,
  pendingDeleteVariantIds,
  deleteIngredient,
  selectedVariants,
  setSelectedVariants,
  productSize,
  recipeResults,
  impactDetails,
  variants,
  staticRecipeIngredientList,
  deleteRecipeVariant,
  getPdfUrl,
  calculateRecipe,
  deleteRecipeIngredient,
  updateIngredientPercentage
}: UseManageRecipeVariantHandlersParams) => {
  const combinedIngredients = useMemo(
    () => buildCombinedIngredients(recipeData, variants, staticRecipeIngredientList),
    [recipeData, variants, staticRecipeIngredientList]
  );

  const handleRemoveVariant = (variantIdToRemove: string) => {
    dispatch({
      type: "SET_PENDING_DELETE_VARIANT_IDS",
      pendingDeleteVariantIds: [variantIdToRemove]
    });
    dispatch({ type: "SET_DELETE_VARIANT", deleteVariant: true });
  };

  const deleteVariants = () => {
    const idsToDelete = [...pendingDeleteVariantIds];
    deleteRecipeVariant(
      {
        recipeId,
        ids: idsToDelete
      },
      {
        onSuccess() {
          recipeDataRefetch();
          dispatch({ type: "SET_PENDING_DELETE_VARIANT_IDS", pendingDeleteVariantIds: [] });
          dispatch({ type: "SET_DELETE_VARIANT", deleteVariant: false });
          setSelectedVariants(prev => prev.filter(v => !idsToDelete.includes(v.id)));
        }
      }
    );
  };

  const downloadPdf = () => {
    if (!recipeData || !recipeData.originalRecipe) {
      return;
    }

    if (recipeData.variants && recipeData.variants.length > 0) {
      if (selectedVariants.length === 0) {
        warningMessage("Please select at least one variant to download the report");
        return;
      }
    }

    const payload = buildDownloadPayload({
      recipeData,
      selectedVariants,
      recipeResults,
      impactDetails,
      productSize,
      combinedIngredients
    });

    getPdfUrl(payload, {
      onSuccess: async (data: any) => {
        // The backend signs the URL with a Content-Disposition filename ending in .pdf
        downloadFileFromUrl(data.url, `${recipeData.recipeName}.pdf`, false);
      }
    });
  };

  const onSaveAndCalculate = (formValues: any) => {
    if (!recipeData || !recipeData.originalRecipe) {
      return;
    }

    calculateRecipe(
      { recipeId: recipeId ?? "", payload: buildCalculatePayload(recipeData, formValues) },
      {
        onSuccess: async () => {
          await refetchRecipeResults();
          recipeDataRefetch();
          dispatch({ type: "SET_SCROLL_TO_BOTTOM", scrollToBottom: true });
        }
      }
    );
  };

  const handleDeleteIngredient = () => {
    deleteRecipeIngredient(
      {
        recipeId: recipeId as string,
        id: deleteIngredient.ingredientId as string,
        type: deleteIngredient.type
      },
      {
        onSuccess: () => {
          dispatch({ type: "SET_DELETE_INGREDIENT", deleteIngredient: null });
          recipeDataRefetch();
        }
      }
    );
  };

  const debouncedUpdateIngredientPercentage = useMemo(
    () =>
      debounce((data: any) => {
        updateIngredientPercentage(data, {
          onSuccess: () => {
            recipeDataRefetch();
          }
        });
      }, 800),
    [updateIngredientPercentage, recipeDataRefetch]
  );

  return {
    handleRemoveVariant,
    deleteVariants,
    downloadPdf,
    onSaveAndCalculate,
    handleDeleteIngredient,
    debouncedUpdateIngredientPercentage
  };
};
